import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_CAREGIVER } from "../../../constants/RegisterConstants";
import { ReferenceType } from "../../../models/document";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { SHOW_ADL_ENTRY_POINTS } from "../../../constants/uiFlags";
import { SHOW_RESIDENT_SCHEDULES } from "../../../constants/uiFlags";
import { dashboardPageContainerStyle } from "./dashboardStyles";
import StaffDelegationRequirementsForm from "../../home/components/StaffDelegationRequirementsForm";
import StaffCredentialDocumentUpload from "../../home/components/StaffCredentialDocumentUpload";
import ResidentEntryTrackerButton from "../../resident/components/ResidentEntryTrackerButton";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { createEmptyStaffDelegationRequirements, getStaffDelegationRequirementsValidationMessage, normalizeStaffDelegationRequirements, StaffDelegationRequirements } from "../../../constants/staffDelegationRequirements";

const DashboardEmployee = () => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;

  const [caregiverResidents, setCaregiverResidents] = useState<any[]>([]);
  const [caregiverResidentsLoading, setCaregiverResidentsLoading] = useState<boolean>(false);
  const [disconnectingResidentId, setDisconnectingResidentId] = useState("");
  const [attendanceActionLoading, setAttendanceActionLoading] = useState<boolean>(false);
  const [attendanceRange, setAttendanceRange] = useState<"today" | "week" | "month">("today");
  const [attendanceSummary, setAttendanceSummary] = useState<any>(null);
  const [currentShift, setCurrentShift] = useState<any>(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  const [caregiverBinderIncomplete, setCaregiverBinderIncomplete] = useState<boolean | null>(null);
  const [showStaffCredentials, setShowStaffCredentials] = useState(false);
  const [staffCredentials, setStaffCredentials] = useState<StaffDelegationRequirements>(createEmptyStaffDelegationRequirements);
  const [staffName, setStaffName] = useState("");
  const [staffEmail, setStaffEmail] = useState("");
  const [savingStaffCredentials, setSavingStaffCredentials] = useState(false);
  const [showCredentialValidationErrors, setShowCredentialValidationErrors] = useState(false);

  const openStaffCredentials = async () => {
    try {
      setShowCredentialValidationErrors(false);
      await backendAPIStore.init();
      const response: any = await backendAPIStore.getStaffAccount(authStore.getUserId());
      setStaffCredentials(normalizeStaffDelegationRequirements(response?.staffDetails || response?.staff?.delegationRequirements));
      setStaffName(`${response?.profile?.firstName || ""} ${response?.profile?.lastName || ""}`.trim() || response?.profile?.email || "Staff");
      setStaffEmail(String(response?.profile?.email || "").trim());
      setShowStaffCredentials(true);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load staff credentials.", life: 3500 });
    }
  };

  const saveStaffCredentials = async () => {
    try {
      setShowCredentialValidationErrors(true);
      const validationMessage = getStaffDelegationRequirementsValidationMessage(staffCredentials);
      if (validationMessage) {
        toast.current?.show({ severity: "error", summary: "Validation required", detail: validationMessage, life: 4500 });
        return;
      }
      setSavingStaffCredentials(true);
      await backendAPIStore.init();
      await backendAPIStore.saveStaffAccount({ roleType: ROLE_TYPE_CAREGIVER, staffId: authStore.getUserId(), name: staffName, email: staffEmail, staffDetails: staffCredentials });
      toast.current?.show({ severity: "success", summary: "Credentials saved", detail: "Your staff credential details were updated.", life: 2500 });
      setShowStaffCredentials(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save staff credentials.", life: 3500 });
    } finally {
      setSavingStaffCredentials(false);
    }
  };

  useEffect(() => {
    const userId = authStore.getUserId();
    if (!userId) {
      return;
    }

    const fetchBinderStatus = async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getBinderByReference(ReferenceType.USER, userId);
        const binderDocuments = Array.isArray(response?.documents) ? response.documents : [];
        const requiredFoldersWithoutFiles = (Array.isArray(response?.folders) ? response.folders : [])
          .filter((folder: any) => folder?.requiredForCompliance === true)
          .some((folder: any) => !binderDocuments.some((document: any) => document?.folderId === folder?.folderId));
        setCaregiverBinderIncomplete(!response?.binder || requiredFoldersWithoutFiles);
      } catch {
        setCaregiverBinderIncomplete(null);
      }
    };

    void fetchBinderStatus();
  }, [authStore, backendAPIStore]);

  useEffect(() => {
    const fetchCaregiverResidents = async () => {
      setCaregiverResidentsLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getCaregiverResidents();
        setCaregiverResidents(Array.isArray(response) ? response.filter((resident: any) => resident?.active !== false) : []);
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message, life: 3000 });
      } finally {
        setCaregiverResidentsLoading(false);
      }
    };
    void fetchCaregiverResidents();
  }, [backendAPIStore]);

  useEffect(() => {
    const backendRange = attendanceRange === "today" ? "week" : attendanceRange;
    const fetchAttendance = async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getCaregiverAttendance({ range: backendRange });
        setAttendanceSummary(response || null);
        setCurrentShift(response?.currentShift || null);
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message, life: 3000 });
      }
    };
    void fetchAttendance();
  }, [attendanceRange, backendAPIStore]);

  const residentRows = useMemo(() => {
    const rows = caregiverResidents.map((resident: any) => ({
      ...resident,
      residentDisplayName: `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || resident?.residentName || "Resident",
      providerDisplayName: String(resident?.providerName || resident?.homeName || "Shared Facility").trim() || "Shared Facility",
      facilityId: String(resident?.homeId || resident?.providerId || "").trim(),
      statusSortKey: resident?.accessActive ? "1-available" : `2-${String(resident?.accessStatus || "outside-shift").trim().toLowerCase()}`,
    }));
    rows.sort((first: any, second: any) => {
      const facilityComparison = String(first?.facilityId || first?.providerDisplayName || "").localeCompare(String(second?.facilityId || second?.providerDisplayName || ""));
      if (facilityComparison !== 0) {
        return facilityComparison;
      }
      const statusComparison = String(first?.statusSortKey || "").localeCompare(String(second?.statusSortKey || ""));
      if (statusComparison !== 0) {
        return statusComparison;
      }
      const residentNameComparison = String(first?.residentDisplayName || "").localeCompare(String(second?.residentDisplayName || ""));
      if (residentNameComparison !== 0) {
        return residentNameComparison;
      }
      return String(first?.providerDisplayName || "").localeCompare(String(second?.providerDisplayName || ""));
    });
    return rows;
  }, [caregiverResidents]);

  const residentFacilityGroups = useMemo(() => {
    const groups = new Map<string, { facilityId: string; facilityName: string; rows: any[] }>();
    residentRows.forEach((resident: any) => {
      const facilityId = String(resident?.facilityId || resident?.homeId || resident?.providerId || "").trim();
      const facilityName = String(resident?.providerDisplayName || resident?.homeName || "Shared Facility").trim() || "Shared Facility";
      const existing = groups.get(facilityId || facilityName) || { facilityId, facilityName, rows: [] };
      existing.rows.push(resident);
      groups.set(facilityId || facilityName, existing);
    });
    return Array.from(groups.values()).sort((first, second) => first.facilityName.localeCompare(second.facilityName));
  }, [residentRows]);

  const clockHomeOptions = useMemo(() => {
    const map = new Map<string, { label: string; value: string; homeName: string }>();
    caregiverResidents.forEach((resident: any) => {
      const homeId = String(resident?.homeId || resident?.providerId || "").trim();
      if (!homeId || map.has(homeId)) {
        return;
      }
      const homeName = String(resident?.providerName || resident?.homeName || "Shared Provider").trim() || "Shared Provider";
      map.set(homeId, { label: homeName, value: homeId, homeName });
    });
    return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [caregiverResidents]);

  const refreshAttendance = async (nextRange: "today" | "week" | "month" = attendanceRange) => {
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getCaregiverAttendance({ range: nextRange === "today" ? "week" : nextRange });
      setAttendanceSummary(response || null);
      setCurrentShift(response?.currentShift || null);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message, life: 3000 });
    }
  };

  const handleClockIn = async (homeId: string) => {
    const selectedHome = clockHomeOptions.find((option) => option.value === homeId);
    if (!selectedHome?.value) {
      toast.current?.show({ severity: "warn", summary: "Select Home", detail: "Choose a home before clocking in.", life: 3000 });
      return;
    }
    setAttendanceActionLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.clockInCaregiverAttendance({
        homeId: selectedHome.value,
        homeName: selectedHome.homeName,
      });
      setCurrentShift(response || null);
      toast.current?.show({ severity: "success", summary: "Clocked In", detail: `Shift started for ${selectedHome.homeName}.`, life: 3000 });
      await refreshAttendance();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message, life: 3000 });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setAttendanceActionLoading(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.clockOutCaregiverAttendance();
      setCurrentShift(null);
      toast.current?.show({ severity: "success", summary: "Clocked Out", detail: "Shift ended and hours were recorded.", life: 3000 });
      await refreshAttendance();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message, life: 3000 });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const runResidentFeatureAccess = (resident: any, action: () => void) => {
    if (resident?.providerSubscriptionActive === false) {
      setShowSubscriptionDialog(true);
      return;
    }
    if (!resident?.accessActive) {
      return;
    }
    action();
  };

  const renderResidentStatus = (rowData: any) => (
    <span
      style={{
        border: rowData?.accessActive ? "1px solid #bbf7d0" : "1px solid #fde68a",
        background: rowData?.accessActive ? "#f0fdf4" : "#fffbeb",
        color: rowData?.accessActive ? "#166534" : "#92400e",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "999px",
        padding: "0.15rem 0.55rem",
        fontSize: "0.74rem",
        fontWeight: 700,
      }}
    >
      {rowData?.accessStatus || (rowData?.accessActive ? "Available Now" : "Outside Shift")}
    </span>
  );

  const confirmDisconnectSharedResident = (resident: any) => {
    const residentId = String(resident?.providerResidentId || resident?.residentId || "").trim();
    const residentName = String(resident?.residentDisplayName || resident?.residentName || "this resident").trim();
    if (!residentId) {
      toast.current?.show({ severity: "error", summary: "Unable to Disconnect", detail: "The shared resident identifier is missing.", life: 3500 });
      return;
    }
    confirmDialog({
      header: "Remove Shared Resident",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div>
          <p className="m-0">Remove access to <strong>{residentName}</strong>?</p>
          <p className="m-0 mt-3">To reconnect, you will need to reach out to the provider and ask them to share the resident again.</p>
        </div>
      ),
      acceptLabel: "Remove Access",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        setDisconnectingResidentId(residentId);
        try {
          await backendAPIStore.disconnectSharedProvider({
            providerResidentId: residentId,
            roleType: ROLE_TYPE_CAREGIVER,
          });
          setCaregiverResidents((current) => current.filter((item: any) => String(item?.providerResidentId || item?.residentId || "").trim() !== residentId));
          toast.current?.show({ severity: "success", summary: "Resident Removed", detail: `${residentName} was removed from your shared residents.`, life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to remove the shared resident.", life: 4000 });
        } finally {
          setDisconnectingResidentId("");
        }
      },
    });
  };

  const renderSharedResidentSettings = (rowData: any) => {
    const residentId = String(rowData?.providerResidentId || rowData?.residentId || "").trim();
    return (
      <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <ActionMenuButton
          label=""
          ariaLabel={`Shared resident settings for ${rowData?.residentDisplayName || "resident"}`}
          icon="pi pi-cog"
          size="small"
          rounded
          outlined
          severity="secondary"
          loading={disconnectingResidentId === residentId}
          style={{ width: "1.9rem", height: "1.9rem", padding: 0 }}
          menuStyle={{ minWidth: "13rem" }}
          model={[{
            label: "Remove Shared Resident",
            icon: "pi pi-unlink",
            command: () => confirmDisconnectSharedResident(rowData),
          }]}
        />
      </span>
    );
  };

  const renderResidentActions = (rowData: any) => (
    <div className="flex flex-wrap gap-2" style={{ rowGap: "0.5rem", minWidth: "16rem" }}>
      {authExperience.featureFlags.providerTools.entryTracker ? <ResidentEntryTrackerButton
        residentId={String(rowData?.residentId || "")}
        residentName={String(rowData?.residentDisplayName || rowData?.residentName || "Resident")}
        residentEntryStatus={rowData?.residentEntryStatus}
        disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false}
        style={{ color: "#075985", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#e0f2fe", border: "1px solid #7dd3fc", borderRadius: "999px" }}
        createEntry={(residentId, payload) => backendAPIStore.createCaregiverResidentMovementEntry(residentId, payload)}
      /> : null}
      {(
        <Button type="button" label="Behavior" text icon="pi pi-clock" iconPos="left" style={{ color: "#075985", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#e0f2fe", border: "1px solid #7dd3fc", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentDailyBehaviorLogPagePath(String(rowData?.residentId || ""), "new")))} />
      )}
      {(
        <Button type="button" label="Incident" text icon="pi pi-exclamation-circle" iconPos="left" style={{ color: "#991b1b", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentIncidentReportPagePath(String(rowData?.residentId || ""), "new")))} />
      )}
      {(
        <Button type="button" label="Progress Notes" text icon="pi pi-pencil" iconPos="left" style={{ color: "#166534", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#dcfce7", border: "1px solid #86efac", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentDailyNotesPagePath(String(rowData?.residentId || ""), "new")))} />
      )}
      {(
        <Button type="button" label="eMAR" text icon="pi pi-table" iconPos="left" style={{ color: "#9a3412", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#ffedd5", border: "1px solid #fdba74", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentEmarPagePath(String(rowData?.residentId || ""))))} />
      )}
      {SHOW_ADL_ENTRY_POINTS && (
        <Button type="button" label="ADL" text icon="pi pi-check-square" iconPos="left" style={{ color: "#4338ca", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentAdlPagePath(String(rowData?.residentId || ""))))} />
      )}
      {SHOW_RESIDENT_SCHEDULES ? <Button type="button" label="Schedules" text icon="pi pi-calendar" iconPos="left" style={{ color: "#075985", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#e0f2fe", border: "1px solid #7dd3fc", borderRadius: "999px" }} disabled={!rowData?.accessActive && rowData?.providerSubscriptionActive !== false} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getResidentSchedulesPagePath(String(rowData?.facilityId || rowData?.homeId || ""), String(rowData?.residentId || ""))))} /> : null}
      <Button type="button" label="Care Plan" text icon="pi pi-search" iconPos="left" style={{ color: "#334155", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "999px" }} disabled={((!rowData?.accessActive && rowData?.providerSubscriptionActive !== false) || rowData?.carePlanAccessEnabled === false)} onClick={() => runResidentFeatureAccess(rowData, () => navigate(RouterAddress.getCaregiverResidentCarePlanPagePath(String(rowData?.residentId || ""))))} />
      {renderSharedResidentSettings(rowData)}
    </div>
  );

  const renderFacilityCareActivityButton = (group: { facilityId: string; facilityName: string; rows: any[] }) => {
    const selectedRow = group.rows.find((row: any) => row?.accessActive) || group.rows[0];
    const isShared = selectedRow?.isShared === true || selectedRow?.providerSource === "shared_connection";
    return (
      <Button
        type="button"
        label="Care Activity"
        icon="pi pi-list-check"
        severity="secondary"
        outlined
        rounded
        size="small"
        disabled={!group.facilityId || !group.rows.some((row: any) => row?.accessActive)}
        onClick={() => runResidentFeatureAccess(selectedRow, () => navigate(isShared
          ? RouterAddress.getSharedHomeCareActivityPagePath(group.facilityId)
          : RouterAddress.getHomeCareActivityPagePath(group.facilityId)))}
      />
    );
  };

  const renderFacilityTasksButton = (group: { facilityId: string; facilityName: string; rows: any[] }) => (
    <Button
      type="button"
      label="Facility Tasks"
      icon="pi pi-check-square"
      severity="warning"
      outlined
      rounded
      size="small"
      disabled={!group.facilityId}
      onClick={() => navigate(
                    authStore.getUserRoleType() === ROLE_TYPE_CAREGIVER
          ? RouterAddress.getCaregiverHomeFacilityTasksPagePath(group.facilityId)
          : RouterAddress.getSharedHomeFacilityTasksPagePath(group.facilityId),
      )}
    />
  );

  const renderFacilityClockButton = (group: { facilityId: string; facilityName: string; rows: any[] }) => {
    const isCurrentFacilityShift = Boolean(currentShift?.homeId) && String(currentShift.homeId) === group.facilityId;
    const employmentProfile = Array.isArray(attendanceSummary?.employmentProfiles)
      ? attendanceSummary.employmentProfiles.find((profile: any) => String(profile?.homeId || "").trim() === group.facilityId)
      : null;
    const outsideShift = !isCurrentFacilityShift && employmentProfile?.clockInAllowed !== true;
    const clockInDisabled = !isCurrentFacilityShift && (Boolean(currentShift) || outsideShift);
    const clockDisabledMessage = outsideShift
      ? "Clock In is unavailable outside your assigned shift."
      : "Clock out your active facility shift before clocking in here.";
    return (
      <span
        className={clockInDisabled ? "caregiver-clock-action-tooltip" : undefined}
        data-pr-tooltip={clockInDisabled ? clockDisabledMessage : undefined}
        data-pr-position="top"
        title={clockInDisabled ? clockDisabledMessage : undefined}
        style={{ display: "inline-flex" }}
      >
        <Button
          type="button"
          label={isCurrentFacilityShift ? "Clock Out" : "Clock In"}
          icon={isCurrentFacilityShift ? "pi pi-stop" : "pi pi-play"}
          severity={isCurrentFacilityShift ? "danger" : "success"}
          className="caregiver-clock-action-button"
          rounded
          size="small"
          style={{
            color: "#ffffff",
            background: isCurrentFacilityShift ? "#dc2626" : "#16a34a",
            borderColor: isCurrentFacilityShift ? "#dc2626" : "#16a34a",
          }}
          loading={attendanceActionLoading}
          disabled={clockInDisabled}
          onClick={() => {
            if (isCurrentFacilityShift) {
              void handleClockOut();
            } else {
              void handleClockIn(group.facilityId);
            }
          }}
        />
      </span>
    );
  };

  return (
    <div className="container-fluid" style={{ padding: mobile ? "0.75rem" : "1rem" }}>
      <Toast ref={toast} />
      <Tooltip
        key={`${residentRows.length}-${currentShift?.homeId || "none"}`}
        target=".caregiver-clock-action-tooltip"
        content="Clock In is unavailable outside your assigned shift."
        position="top"
        showDelay={150}
      />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="Shared resident tools"
        message="This shared resident feature is unavailable because the provider subscription is inactive."
        showBillingButton={false}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <div style={dashboardPageContainerStyle}>
        <div className="flex align-items-center justify-content-between gap-3 flex-wrap" style={{ marginBottom: "0.85rem" }}>
          <div>
            <div style={{ fontSize: mobile ? "1.25rem" : "1.5rem", fontWeight: 800, color: "#0f172a" }}>Caregiver Dashboard</div>
            <div style={{ color: "#64748b", marginTop: "0.25rem", fontSize: "0.9rem" }}>Shared residents and facility care tools</div>
          </div>
          <div className="flex align-items-center justify-content-end gap-2 flex-wrap" style={{ marginLeft: mobile ? 0 : "auto", width: mobile ? "100%" : undefined }}>
            <Button
              type="button"
              label="Manage Credentials"
              icon="pi pi-id-card"
              severity="warning"
              rounded
              outlined
              size="small"
              style={mobile ? { flex: 1 } : undefined}
              onClick={() => void openStaffCredentials()}
            />
            <Button
              type="button"
              label="Binder"
              icon="pi pi-book"
              severity="warning"
              rounded
              outlined
              size="small"
              className={caregiverBinderIncomplete ? "caregiver-binder-incomplete-button" : undefined}
              tooltip={caregiverBinderIncomplete ? "Complete your Binder" : undefined}
              tooltipOptions={{ position: "top" }}
              style={mobile ? { flex: 1 } : undefined}
              onClick={() => navigate(RouterAddress.getCaregiverJobBinderPagePath())}
            />
            <Button
              type="button"
              label="Shift Details"
              icon="pi pi-clock"
              severity="secondary"
              rounded
              outlined
              size="small"
              style={mobile ? { flex: 1 } : undefined}
              onClick={() => navigate(RouterAddress.getCaregiverAttendancePagePath(attendanceRange))}
            />
          </div>
        </div>
        {false && (
          false && (
            <div className="flex justify-content-center p-2">
              <ProgressSpinner role="status" style={{ width: "32px", height: "32px" }} />
            </div>
          ) || (
            <div
              className="flex align-items-center justify-content-center gap-2 flex-wrap"
              style={{
                position: "relative",
                width: "100%",
                flexDirection: mobile || !clockHomeOptions.length ? "column" : "row",
                alignItems: mobile ? "stretch" : "center",
              }}
            >
              {!clockHomeOptions.length ? (
                <div className="flex align-items-center justify-content-center" style={{ width: mobile ? "100%" : undefined, color: "black", fontSize: "1rem", textAlign: "center" }}>
                  Once your employer shares a resident or facility with you, you’ll be able to access it here. In the meantime, you can manage your Binder and share with employers or people.
                </div>
              ) : null}
            </div>
          )
        )}
        <Dialog
          visible={showStaffCredentials}
          modal
          header="Manage Credentials"
          style={mobile ? { width: "96vw" } : { width: "54rem", maxWidth: "95vw" }}
          onHide={() => !savingStaffCredentials && setShowStaffCredentials(false)}
          footer={<div className="flex gap-2 w-full" style={{ flexDirection: mobile ? "column-reverse" : "row", justifyContent: "space-between" }}><Button type="button" label="Cancel" severity="secondary" outlined rounded disabled={savingStaffCredentials} onClick={() => setShowStaffCredentials(false)} /><Button type="button" label="Save Credentials" icon="pi pi-save" severity="warning" rounded loading={savingStaffCredentials} onClick={() => void saveStaffCredentials()} /></div>}
        >
          <StaffDelegationRequirementsForm value={staffCredentials} onChange={setStaffCredentials} showValidationErrors={showCredentialValidationErrors} name={staffName} email={staffEmail} documentUpload={<StaffCredentialDocumentUpload
            residentIds={caregiverResidents.map((resident) => String(resident?.residentId || "").trim()).filter(Boolean)}
            staffName={staffName}
            staffId={authStore.getUserId()}
            email={staffEmail}
            onExtracted={(details) => setStaffCredentials((current) => ({ ...current, ...details }))}
          />} />
        </Dialog>
        <div>
          {caregiverResidentsLoading ? (
            <div className="card flex justify-content-center p-5">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
            </div>
          ) : residentRows.length === 0 ? null : (
            <div style={{ color: "#0f172a", marginBottom: "1rem" }}>
              <div className="flex align-items-center justify-content-between gap-2 flex-wrap" style={{ marginBottom: "0.9rem", textAlign: mobile ? "center" : undefined }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b" }}>
                  Shared Residents
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "999px",
                    padding: "0.22rem 0.6rem",
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    border: "1px solid #bae6fd",
                    background: "#f0f9ff",
                    color: "#0c4a6e",
                  }}
                >
                  {residentRows.length} Residents
                </span>
              </div>
              <div className="hidden md:block">
                <div className="flex flex-column gap-4">
                  {residentFacilityGroups.map((group) => (
                    <div key={group.facilityId || group.facilityName}>
                      <div className="flex align-items-center justify-content-between gap-2 flex-wrap mb-2" style={{ paddingBottom: "0.6rem", borderBottom: "1px solid #e2e8f0" }}>
                        <span style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>{group.facilityName}</span>
                        <div className="flex align-items-center gap-2 flex-wrap">
                          {renderFacilityClockButton(group)}
                          {renderFacilityCareActivityButton(group)}
                          {renderFacilityTasksButton(group)}
                        </div>
                      </div>
                      <DataTable value={group.rows} paginator={group.rows.length > 8} rows={10} emptyMessage="No residents in this facility.">
                        <Column field="residentDisplayName" header="Resident" sortable body={(rowData: any) => (
                          <div>
                            <div>{rowData?.residentDisplayName || "Resident"}</div>
                            {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap mt-1">
                              {SHOW_RESIDENT_SCHEDULES ? <><Tag value={`Schedules ${Number(rowData?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
                              {Number(rowData?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number(rowData?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}</> : null}
                            </div> : null}
                          </div>
                        )} />
                        <Column header="Shift" body={(rowData: any) => rowData?.shiftSummary || "No shift set"} />
                        <Column header="Status" body={renderResidentStatus} />
                        <Column header="Actions" body={renderResidentActions} />
                      </DataTable>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-column gap-3 md:hidden">
                {residentFacilityGroups.map((group) => (
                  <div key={group.facilityId || group.facilityName}>
                    <div className="flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                      <span style={{ fontWeight: 800, color: "#0f172a" }}>{group.facilityName}</span>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        {renderFacilityClockButton(group)}
                        {renderFacilityCareActivityButton(group)}
                        {renderFacilityTasksButton(group)}
                      </div>
                    </div>
                    <div className="flex align-items-center justify-content-between gap-2" style={{ color: "#94a3b8", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", padding: "0.25rem 0.15rem" }}>
                      <span>Resident</span>
                      <span>Status</span>
                    </div>
                    <div className="flex flex-column gap-2">
                      {group.rows.map((rowData: any) => (
                        <div key={`${rowData?.residentId || rowData?.residentDisplayName}-${rowData?.facilityId}`}>
                          <div
                            style={{
                              borderBottom: "1px solid #e2e8f0",
                              padding: "0.55rem 0.15rem 0.75rem",
                            }}
                          >
                            <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                              <div>
                                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>{rowData?.residentDisplayName || "Resident"}</div>
                                {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap mt-1">
                                  {SHOW_RESIDENT_SCHEDULES ? <><Tag value={`Schedules ${Number(rowData?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
                                  {Number(rowData?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number(rowData?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}</> : null}
                                </div> : null}
                              </div>
                              <div className="flex align-items-center gap-2">
                                {renderResidentStatus(rowData)}
                              </div>
                            </div>
                            <div className="mt-2" style={{ color: "#475569", fontSize: "0.85rem" }}>
                              <span style={{ fontWeight: 700 }}>Shift:</span> {rowData?.shiftSummary || "No shift set"}
                            </div>
                            <div className="mt-2">
                              <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.4rem" }}>
                                Actions
                              </div>
                              {renderResidentActions(rowData)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardEmployee;
